import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  ListOportunidadesParams,
  OportunidadeStatus,
  Prioridade,
} from '../api';

const VALID_STATUS: OportunidadeStatus[] = [
  'aberta',
  'ganha',
  'perdida',
  'suspensa',
  'cancelada',
];

const VALID_PRIORIDADE: Prioridade[] = ['baixa', 'media', 'alta', 'critica'];

/**
 * Filtros compartilhados entre as visões Lista, Cards e Kanban
 * (US-COM-004 §Filtros e Visões, task 14.1). O estado vive na query
 * string — alternar entre visões preserva os filtros e a URL fica
 * shareável (útil para deep-links em emails/tickets).
 *
 * Retorna `params` prontos para o `useOportunidades` + setters granulares.
 * Filtros de período e paginação ficam com a página consumidora (a Lista
 * paginа, Cards paginа, Kanban não pagina).
 */
export function useOportunidadeFilters() {
  const [sp, setSp] = useSearchParams();

  const filters = useMemo<
    Pick<ListOportunidadesParams, 'search' | 'status' | 'prioridade' | 'stageId' | 'responsavelId' | 'clientId'>
  >(() => {
    const rawStatus = sp.get('status') as OportunidadeStatus | null;
    const rawPrioridade = sp.get('prioridade') as Prioridade | null;
    return {
      search: sp.get('search') || undefined,
      status:
        rawStatus && (VALID_STATUS as string[]).includes(rawStatus) ? rawStatus : undefined,
      prioridade:
        rawPrioridade && (VALID_PRIORIDADE as string[]).includes(rawPrioridade)
          ? rawPrioridade
          : undefined,
      stageId: sp.get('stageId') || undefined,
      responsavelId: sp.get('responsavelId') || undefined,
      clientId: sp.get('clientId') || undefined,
    };
  }, [sp]);

  const setFilter = (
    key: 'search' | 'status' | 'prioridade' | 'stageId' | 'responsavelId' | 'clientId',
    value: string | undefined,
  ) => {
    const next = new URLSearchParams(sp);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSp(next, { replace: true });
  };

  const resetFilters = () => {
    // Preserva params de outras naturezas (ex: `view=` se algum dia usar).
    const next = new URLSearchParams(sp);
    (['search', 'status', 'prioridade', 'stageId', 'responsavelId', 'clientId'] as const).forEach(
      (k) => next.delete(k),
    );
    setSp(next, { replace: true });
  };

  return { filters, setFilter, resetFilters };
}
